import { useCallback, useState } from "react";
import { readParquetFile } from "../utils/parquet-utils";

/**
 * useFileHandler hook to manage file reading and processing.
 * Reference to repo: https://github.com/noworneverev/graphrag-visualizer
 */

//Maps file names to their corresponding schema names.
const baseMapping = {
  "entities.parquet": "entity",
  "relationships.parquet": "relationship",
  "documents.parquet": "document",
  "text_units.parquet": "text_unit",
  "communities.parquet": "community",
  "community_reports.parquet": "community_report",
  "covariates.parquet": "covariate",
};

const fileSchemas = {};
Object.entries(baseMapping).forEach(([key, value]) => {  
  fileSchemas[key] = value;  
  // 'create_final_' prefix is used for intermediate files. Adding this allows user to use intermediate files in the same way as final files. Not yet implemented in backend.
  fileSchemas[`create_final_${key}`] = value;
});

/**
 * Custom hook to handle file uploads and data processing.
 * @returns {Object} - An arrayMap containing processed data from the files, the handleFilesRead function to read files.
 */
const useFileHandler = () => {
  const [arrayMap, setArrayMap] = useState([]);
  
  const loadFiles = useCallback(async (files) => {
    const entitiesArray = [];
    const relationshipsArray = [];
    const documentsArray = [];
    const textUnitsArray = [];
    const communitiesArray = [];
    const communityReportsArray = [];
    const covariatesArray = [];
    const arrayMap = {
            entities: entitiesArray,
            relationships: relationshipsArray,
            documents: documentsArray,
            text_units: textUnitsArray,
            communities: communitiesArray,
            community_reports: communityReportsArray,
            covariates: covariatesArray,
        };

    for (const file of files) {
      const fileName = typeof file === "string" ? file.split("/").pop() : file.name;      
      const schema = fileSchemas[fileName] || fileSchemas[`create_final_${fileName}`];

      let data;
        data = await readParquetFile(file, schema);
      // TODO: simplify this by 1. Unify schema name to singular/plural 2. use arrayMap[schema].push(data); to push data for all schemas
      if (schema === "entity") {
        entitiesArray.push(...data);
      }
      else if (schema === "relationship") {
        relationshipsArray.push(...data);
      } else if (schema === "document") {
        documentsArray.push(...data);
      } else if (schema === "text_unit") {
        textUnitsArray.push(...data);
      } else if (schema === "community") {
        communitiesArray.push(...data);
      } else if (schema === "community_report") {
        communityReportsArray.push(...data);
      } else if (schema === "covariate") {
        covariatesArray.push(...data);
      } else {
        console.warn(`Unknown schema for file ${fileName}: ${schema}`);
      }
    }
    setArrayMap(arrayMap);
  }, []);

  const handleFilesRead = useCallback(async (files) => {
    await loadFiles(files);
  }, [loadFiles]);

  return {
    arrayMap,
    setArrayMap,
    handleFilesRead,
  };
};

export default useFileHandler;
